import type { FloraCatalog } from '@/flora/FloraCatalog';
import { FLORA_CATEGORIES } from '@/flora/FloraDefinition';
import type { EnvironmentManager } from '@/environment/EnvironmentManager';
import type { SimpleSeason } from '@/environment/EnvironmentState';
import type { FloraResolver } from '@/localization/FloraResolver';
import {
  defaultLocalization,
  zoneLabel,
  type SiteLocalization,
} from '@/localization/SiteLocalization';
import { primaryWarning, suitabilityBadge, suitabilityLabel } from '@/localization/Suitability';
import { MONTH_LABELS } from '@/localization/Ecology';
import { clear, el, formatLength, setClass, type Child } from './dom';
import { filledIcon, icon } from './Icons';
import { FloraCard } from './FloraCard';
import { DateScrubber } from './DateScrubber';
import { floraThumbnailSvg, safeColor } from './FloraThumbnail';
import { percentLabel } from './FloraFactsheet';
import type { UiPreferences } from './UiPreferences';
import {
  COLOR_FAMILIES,
  COLOR_FAMILY_SWATCHES,
  FORM_FACTORS,
  activeFilterCount,
  categoryLabel,
  colorFamiliesOf,
  colorFamilyLabel,
  defaultFloraFilter,
  filterFlora,
  formFactorLabel,
  formFactorOf,
  genusFacets,
  tagFacets,
  toggleFacet,
  type ColorFamily,
  type FloraFilter,
  type FormFactor,
} from './FloraFacets';
import {
  COMPARE_COLORS,
  COMPARE_LIMIT,
  DATABASE_LENSES,
  TABLE_GROUPS,
  ZONE_AXIS,
  buildDatabaseRows,
  columnsFor,
  compareSections,
  databaseCsv,
  databaseSummary,
  ecologyRollup,
  monthFacets,
  paletteRiver,
  scalePoints,
  sortRows,
  suitabilityBands,
  zoneSpanOf,
  type DatabaseLens,
  type DatabaseRow,
  type SortDirection,
  type TableColumn,
  type TableGroupId,
} from './FloraDatabase';
import {
  feedbackPrompt,
  feedbackTitle,
  localFeedbackChannel,
  submitFeedback,
  type FeedbackChannel,
  type FeedbackDraft,
} from './Feedback';
import { Dialog, dialogButton, dialogField, textInput } from './Dialog';

/**
 * **The Flora Database** — the catalog as a place you go, rather than a list you scroll.
 *
 * ## The question it exists for
 *
 * Three surfaces already read the flora records and each answers a different question. The palette
 * answers *what do I place next*; the record card answers *everything about this one plant*; the
 * manifest answers *what did I plant*. None of them answers the one a designer actually asks while
 * choosing: **how do these plants compare — with each other, and with this site?**
 *
 * That is a comparison problem, and comparison is the one thing a scrolling grid of tiles is worst
 * at. So this is a full sheet, and it holds one selection read six ways.
 *
 * ## One query, six lenses
 *
 * The rail on the left is the *query* and it never changes when you switch lenses — the same
 * {@link FloraFilter} the palette runs, through the same `filterFlora`. The stage on the right is
 * the *lens*, and the lens is only ever a way of drawing the rows the query returned:
 *
 * | Lens | The question |
 * | --- | --- |
 * | Gallery | What do these look like? |
 * | Table | What exactly does the record say? |
 * | Year | What happens, and when, and in what colour? |
 * | Site | What will live here, and how big will it get? |
 * | Wildlife | What does this palette feed and shelter? |
 * | Compare | How do these four differ, parameter by parameter? |
 *
 * Focus survives the switch. Narrow to five shrubs in the Gallery, press Year, and it is those five
 * shrubs — which is the whole reason the query is a rail rather than a control inside each lens.
 *
 * ## Nothing here is a second opinion
 *
 * Every figure comes from `FloraDatabase.ts`, which in turn reads the resolver, the growth model and
 * the appearance model the canvas itself runs. The ⓘ on every plate and row opens the *same*
 * {@link FloraCard} the palette opens, not a copy of it; the Compare lens is the record card's own
 * factsheet transposed. There is no drawing on this screen that could disagree with the viewport
 * behind it, because there is no second calculation anywhere in it.
 *
 * ## Colour
 *
 * Bloom, foliage and fruit are drawn in the plant's own recorded hex — literal, not decorative, and
 * the reason the Year lens works as a design instrument. Magnitude is one hue at varying strength.
 * Suitability uses the HUD's existing good/warning/danger trio and always carries its word, so the
 * meaning never rests on colour alone. The one categorical palette is {@link COMPARE_COLORS}, four
 * fixed slots assigned by pin order.
 *
 * ## On the facet rail
 *
 * Its markup is its own, not `FloraBrowser`'s, while every decision it makes goes through the shared
 * `FloraFacets` model. The two rails genuinely differ — this one counts against the whole catalog
 * and offers facets the palette has no room for — and sharing the *model* is what keeps them
 * honest. Sharing the markup would only have kept them identical.
 */

export interface FloraDatabasePanelDeps {
  catalog: FloraCatalog;
  preferences: UiPreferences;
  /** Current season, so silhouettes match the canvas behind the sheet. */
  season: () => SimpleSeason;
  /** The scene's day of year, which drives the record card's 3D preview. */
  dayOfYear?: () => number;
  /**
   * The scene's environment, which turns the two read-only getters above into a *control*: given it,
   * the header grows a date scrubber and the sheet becomes a place you can move the year from.
   *
   * It is the same object the floating {@link ChronoBar} writes to, so there is one date in the app
   * and this is another handle on it — see {@link DateScrubber}. Omit it and the sheet still reads
   * the season, it just cannot change it.
   */
  environment?: EnvironmentManager;
  /**
   * The scrubber moved the date. The host repaints the scene and the rest of the HUD, and calls
   * {@link refresh} back on this panel — which is what redraws the plates for the new season.
   */
  onDateChange?: () => void;
  /** The site's resolver. Always present — unlocalized sites resolve against the reference climate. */
  resolver: () => FloraResolver;
  /** The site profile, or null when it has never been localized. */
  localization: () => SiteLocalization | null;
  /** Arm a plant for placement. The database is a library you can plant straight out of. */
  onPick?: (definitionId: string) => void;
  /**
   * The plant already in hand, so a plate can show itself picked.
   *
   * The same reading the palette's tiles take, from the same place — which is what keeps the two
   * surfaces agreeing about which plant is armed rather than each keeping its own idea of it.
   */
  activeId?: () => string | null;
  /** Where suggestions go. Defaults to the local queue — see `Feedback.ts`. */
  feedback?: FeedbackChannel;
  /** Build string stamped onto a suggestion, so a report names the app it came from. */
  appVersion?: string;
  /** The sheet opened or closed, so the launching button can show itself pressed. */
  onOpenChange?: (open: boolean) => void;
}

/** Rows added per "show more" press in the table. */
const TABLE_PAGE_SIZE = 120;

export class FloraDatabasePanel {
  readonly root: HTMLElement;

  private lens: DatabaseLens = 'gallery';
  private filter: FloraFilter = defaultFloraFilter();
  private sortColumn = 'name';
  private sortDirection: SortDirection = 'ascending';
  private groupsOn: TableGroupId[] = TABLE_GROUPS.filter((g) => g.defaultOn).map((g) => g.id);
  private pinned: string[] = [];
  private focusId: string | null = null;
  private tableLimit = TABLE_PAGE_SIZE;
  /** True while the rail is showing every facet rather than the four it opens with. */
  private allFacets = false;

  private readonly scrim: HTMLElement;
  private readonly sheet: HTMLElement;
  private readonly countEl: HTMLElement;
  private readonly siteChip: HTMLElement;
  private readonly lensBar: HTMLElement;
  private readonly searchInput: HTMLInputElement;
  private readonly toolsEl: HTMLElement;
  private readonly railEl: HTMLElement;
  private readonly stageEl: HTMLElement;
  private readonly summaryEl: HTMLElement;
  private readonly pinTray: HTMLElement;
  private readonly noteEl: HTMLElement;
  /** The date handle in the header. Null when the host gave the sheet no environment to write to. */
  private readonly dateScrubber: DateScrubber | null;
  private readonly card: FloraCard;
  private readonly channel: FeedbackChannel;
  private feedbackDialog: Dialog | null = null;

  /** Escape unwinds one layer at a time: the record card, then a query, then the sheet. */
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    // The card claims Escape first through its own capture listener; if it is open, let it have it.
    if (this.card.openId !== null) return;
    event.stopPropagation();
    event.preventDefault();
    if (activeFilterCount(this.filter) > 0) {
      this.setFilter(defaultFloraFilter());
      this.searchInput.value = '';
      return;
    }
    this.close();
  };

  constructor(private readonly deps: FloraDatabasePanelDeps) {
    this.channel = deps.feedback ?? localFeedbackChannel();

    this.card = new FloraCard({
      // The sheet blurs its backdrop, which would pin a fixed child inside it — so the card mounts
      // in this panel's own unfiltered root, one layer above the sheet.
      host: () => this.root,
      preferences: deps.preferences,
      season: () => deps.season(),
      ...(deps.dayOfYear ? { dayOfYear: (): number => deps.dayOfYear?.() ?? 172 } : {}),
      ...(deps.onPick ? { onPick: (id: string): void => this.pick(id) } : {}),
      onFavoriteChange: () => this.renderStage(),
      onOpenChange: () => this.markCardActive(),
      resolver: () => deps.resolver(),
    });

    this.countEl = el('span', { class: 'lg-flodb__count' });
    this.siteChip = el('button', {
      class: 'lg-flodb__site',
      attrs: { type: 'button' },
      on: { click: () => this.setLens('site') },
    });

    this.lensBar = el('div', {
      class: 'lg-segment lg-segment--tight lg-flodb__lenses',
      attrs: { role: 'group', 'aria-label': 'Database lens' },
    });
    for (const option of DATABASE_LENSES) {
      this.lensBar.appendChild(
        el('button', {
          class: 'lg-segment__item',
          text: option.label,
          title: option.hint,
          attrs: { type: 'button', 'data-lens': option.value },
          on: { click: () => this.setLens(option.value) },
        }),
      );
    }

    this.searchInput = el('input', {
      class: 'lg-flodb__search',
      attrs: {
        type: 'search',
        placeholder: 'Search the catalog — name, genus, trait…',
        'aria-label': 'Search the flora catalog',
        spellcheck: 'false',
      },
      on: { input: () => this.setFilter({ search: this.searchInput.value }) },
    });

    this.toolsEl = el('div', { class: 'lg-flodb__tools' });
    this.railEl = el('aside', {
      class: 'lg-flodb__rail',
      attrs: { 'aria-label': 'Narrow the catalog' },
    });
    this.stageEl = el('section', { class: 'lg-flodb__stage' });
    this.summaryEl = el('span', { class: 'lg-flodb__summary' });
    this.pinTray = el('div', { class: 'lg-flodb__pins' });
    this.noteEl = el('span', { class: 'lg-flodb__note' });

    /*
     * The date, in the header rather than in the tool bar below it.
     *
     * Every plate on this sheet is a picture of a moment in the year — the silhouettes are painted
     * from each plant's own seasonal palette — so the sheet has a date whether or not it admits to
     * one, and the honest thing is to put the handle for it where the plates can be watched while
     * it moves. It is not a filter, which is why it is not on the query bar with the facets: it
     * changes what you are looking at, not which plants you are looking at.
     *
     * The scrubber writes to the environment, so dragging it here is the same act as dragging the
     * floating bar over the scene — the viewport, the bar, and these cards all move together.
     */
    this.dateScrubber = deps.environment
      ? new DateScrubber({
          environment: deps.environment,
          label: 'Scene date — the season these cards are drawn in',
          onChange: () => {
            // The host coalesces into the next frame and calls `refresh()` back here, which is what
            // repaints the plates. Standing alone there is nobody to do that, so do it directly.
            if (deps.onDateChange) deps.onDateChange();
            else this.render();
          },
        })
      : null;

    this.sheet = el(
      'div',
      { class: 'lg-flodb__sheet', attrs: { role: 'dialog', 'aria-label': 'Flora database' } },
      el(
        'header',
        { class: 'lg-flodb__head' },
        el('span', { class: 'lg-flodb__headicon', html: icon('layers', 17) }),
        el(
          'div',
          { class: 'lg-flodb__heading' },
          el('h2', { class: 'lg-flodb__title', text: 'Flora database' }),
          el('span', {
            class: 'lg-flodb__subtitle',
            text: `Catalog ${deps.catalog.version ?? ''}`.trim(),
          }),
        ),
        this.countEl,
        this.siteChip,
        // A spacer either side, so the scrubber sits in the middle of the room the header has left
        // rather than at a fixed offset that could land on top of a long region name.
        el('div', { class: 'lg-flodb__spacer' }),
        this.dateScrubber?.root ?? null,
        el('div', { class: 'lg-flodb__spacer' }),
        this.actionButton('plus', 'Suggest a plant', 'Tell the catalog’s maintainers what is missing', () =>
          this.openFeedback({ scope: 'catalog', message: '' }),
        ),
        el('button', {
          class: 'lg-flodb__iconbutton',
          html: icon('close', 16),
          title: 'Close  ·  Esc',
          attrs: { type: 'button', 'aria-label': 'Close the flora database' },
          on: { click: () => this.close() },
        }),
      ),
      el(
        'div',
        { class: 'lg-flodb__bar' },
        this.lensBar,
        el(
          'div',
          { class: 'lg-flodb__searchwrap' },
          el('span', { class: 'lg-flodb__searchicon', html: icon('search', 14) }),
          this.searchInput,
        ),
        this.toolsEl,
      ),
      el('div', { class: 'lg-flodb__main' }, this.railEl, this.stageEl),
      el(
        'footer',
        { class: 'lg-flodb__foot' },
        this.summaryEl,
        this.noteEl,
        el('div', { class: 'lg-flodb__spacer' }),
        this.pinTray,
      ),
    );

    this.scrim = el('div', {
      class: 'lg-flodb__scrim',
      on: { click: () => this.close() },
    });

    this.root = el(
      'div',
      { class: 'lg-flodb', attrs: { hidden: true } },
      this.scrim,
      this.sheet,
    );

    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.onKeyDown, true);
    }
  }

  /* --------------------------------- Open and close --------------------------------- */

  get isOpen(): boolean {
    return this.root.hidden === false;
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  open(lens?: DatabaseLens): void {
    if (lens) this.lens = lens;
    this.root.hidden = false;
    this.tableLimit = TABLE_PAGE_SIZE;
    this.render();
    this.searchInput.focus();
    this.deps.onOpenChange?.(true);
  }

  close(): void {
    if (!this.isOpen) return;
    this.card.close();
    this.root.hidden = true;
    this.deps.onOpenChange?.(false);
  }

  /** Redraw for outside changes — the season moved, units switched, the site was localized. */
  refresh(): void {
    if (!this.isOpen) return;
    this.render();
    this.card.refresh();
  }

  /* ------------------------------------- State -------------------------------------- */

  private get unit(): 'meters' | 'feet' {
    return this.deps.preferences.unit;
  }

  private get localization(): SiteLocalization {
    return this.deps.localization() ?? defaultLocalization();
  }

  private get isLocalized(): boolean {
    return this.deps.localization() !== null;
  }

  /** Every catalog record, read against the site. Rebuilt per render; the resolver caches behind it. */
  private allRows(): DatabaseRow[] {
    return buildDatabaseRows(this.deps.catalog.list(), {
      resolver: this.deps.resolver(),
      favorites: this.deps.preferences.favorites,
    });
  }

  /** The rows the query returned, in the current sort. */
  private rows(): DatabaseRow[] {
    const context = { favorites: this.deps.preferences.favorites, resolver: this.deps.resolver() };
    const matched = filterFlora(this.deps.catalog.list(), this.filter, context);
    const ids = new Set(matched.map((definition) => definition.id));
    return sortRows(
      this.allRows().filter((row) => ids.has(row.id)),
      this.sortColumn,
      this.sortDirection,
    );
  }

  private setLens(lens: DatabaseLens): void {
    this.lens = lens;
    this.tableLimit = TABLE_PAGE_SIZE;
    this.render();
    // A new lens is a new document. Only *focusing* inside one holds its place — see `renderStage`.
    this.stageEl.scrollTop = 0;
  }

  private setFilter(patch: Partial<FloraFilter>): void {
    this.filter = { ...this.filter, ...patch };
    this.tableLimit = TABLE_PAGE_SIZE;
    this.render();
    this.stageEl.scrollTop = 0;
  }

  private setFocus(id: string | null): void {
    this.focusId = this.focusId === id ? null : id;
    this.renderStage();
  }

  /** The plant currently in hand, when the host tracks one. */
  private get armedId(): string | null {
    return this.deps.activeId?.() ?? null;
  }

  /**
   * Pick a plant up off the shelf.
   *
   * The gallery's plates *are* the palette's tiles at full size, so they do what a tile does:
   * clicking one arms it on the plant tool and lights the same plant up in the palette behind the
   * sheet. The sheet stays open, because at this size you are usually still choosing — the record
   * card's Plant button remains the one control that both arms a plant and gets out of the way.
   *
   * Rows in the other lenses only take focus. Those are charts, and a click on a chart row means
   * "show me this one", not "give me this one".
   */
  private choose(id: string): void {
    this.focusId = id;
    this.deps.onPick?.(id);
    // The host coalesces its own refresh into the next frame; redraw now so the plate lights under
    // the cursor that pressed it rather than a beat later.
    this.renderStage();
  }

  private pick(id: string): void {
    this.focusId = id;
    this.deps.onPick?.(id);
    this.close();
  }

  private togglePin(id: string): void {
    if (this.pinned.includes(id)) this.pinned = this.pinned.filter((entry) => entry !== id);
    else if (this.pinned.length < COMPARE_LIMIT) this.pinned = [...this.pinned, id];
    else {
      this.flashNote(`Compare holds ${COMPARE_LIMIT} plants — unpin one first.`);
      return;
    }
    this.render();
  }

  /* ------------------------------------ Rendering ----------------------------------- */

  private render(): void {
    if (!this.isOpen) return;
    const rows = this.rows();

    for (const button of this.lensBar.querySelectorAll('button')) {
      button.classList.toggle('is-active', button.dataset.lens === this.lens);
    }
    this.countEl.textContent = `${rows.length} / ${this.deps.catalog.size}`;
    // Cheap and DOM-stable, so it can follow every render: the date moves from the floating bar and
    // from loading a project as readily as from the handle in this header, and it holds its own
    // value while it is being dragged.
    this.dateScrubber?.refresh();
    this.renderSiteChip();
    this.renderTools();
    this.renderRail(rows);
    this.renderStage(rows);
    this.summaryEl.textContent = databaseSummary(rows, this.deps.catalog.size, this.isLocalized);
    this.renderPins();
  }

  private renderSiteChip(): void {
    const localization = this.deps.localization();
    clear(this.siteChip);
    if (!localization) {
      this.siteChip.className = 'lg-flodb__site is-unset';
      this.siteChip.title = 'This site has no location yet — nothing here is rated against a climate';
      this.siteChip.append(
        el('span', { class: 'lg-flodb__sitedot' }),
        el('span', { text: 'Not localized' }),
      );
      return;
    }
    this.siteChip.className = 'lg-flodb__site';
    this.siteChip.title = `Every site figure below is resolved for ${localization.regionLabel}`;
    this.siteChip.append(
      el('span', { class: 'lg-flodb__sitedot' }),
      el('span', { text: localization.regionLabel }),
      el('span', { class: 'lg-flodb__sitezone', text: `Zone ${zoneLabel(localization.hardinessZone)}` }),
    );
  }

  /** The right-hand tool cluster — different per lens, because the controls a lens needs differ. */
  private renderTools(): void {
    clear(this.toolsEl);
    if (this.lens === 'table') {
      for (const group of TABLE_GROUPS) {
        const on = this.groupsOn.includes(group.id);
        this.toolsEl.appendChild(
          el('button', {
            class: `lg-chip lg-chip--tight${on ? ' is-active' : ''}`,
            text: group.label,
            title: group.hint,
            attrs: { type: 'button', 'aria-pressed': on },
            on: {
              click: () => {
                this.groupsOn = toggleFacet(this.groupsOn, group.id);
                this.render();
              },
            },
          }),
        );
      }
      this.toolsEl.appendChild(
        this.actionButton('download', 'CSV', 'Download exactly these columns and rows', () =>
          this.downloadCsv(),
        ),
      );
      return;
    }

    if (this.lens === 'gallery' || this.lens === 'year' || this.lens === 'site') {
      this.toolsEl.appendChild(this.sortControl());
    }
  }

  private sortControl(): HTMLElement {
    const options: { id: string; label: string }[] = [
      { id: 'name', label: 'Common name' },
      { id: 'botanical', label: 'Botanical name' },
      { id: 'height', label: 'Height here' },
      { id: 'bloom', label: 'First bloom' },
      { id: 'eco-value', label: 'Ecological value' },
      ...(this.isLocalized ? [{ id: 'suitability', label: 'Best for this site' }] : []),
    ];
    const select = el('select', {
      class: 'lg-flodb__select',
      attrs: { 'aria-label': 'Sort' },
      on: {
        change: (event) => {
          this.sortColumn = (event.target as HTMLSelectElement).value;
          this.render();
        },
      },
    });
    for (const option of options) {
      select.appendChild(
        el('option', {
          text: option.label,
          attrs: { value: option.id, selected: option.id === this.sortColumn },
        }),
      );
    }
    return el(
      'label',
      { class: 'lg-flodb__sortwrap' },
      el('span', { class: 'lg-flodb__sortlabel', text: 'Sort' }),
      select,
      el('button', {
        class: 'lg-flodb__iconbutton lg-flodb__iconbutton--small',
        html: icon('chevron', 13),
        title: this.sortDirection === 'ascending' ? 'Ascending — click for descending' : 'Descending',
        attrs: {
          type: 'button',
          'aria-label': 'Reverse the sort',
          'data-direction': this.sortDirection,
        },
        on: {
          click: () => {
            this.sortDirection = this.sortDirection === 'ascending' ? 'descending' : 'ascending';
            this.render();
          },
        },
      }),
    );
  }

  private actionButton(
    iconName: Parameters<typeof icon>[0],
    label: string,
    title: string,
    onClick: () => void,
  ): HTMLElement {
    return el(
      'button',
      { class: 'lg-flodb__action', title, attrs: { type: 'button' }, on: { click: onClick } },
      el('span', { class: 'lg-flodb__actionicon', html: icon(iconName, 13) }),
      el('span', { text: label }),
    );
  }

  /* ------------------------------------ The rail ------------------------------------ */

  private renderRail(rows: readonly DatabaseRow[]): void {
    clear(this.railEl);
    const all = this.deps.catalog.list();
    const active = activeFilterCount(this.filter);

    this.railEl.appendChild(
      el(
        'div',
        { class: 'lg-flodb__railhead' },
        el('span', { class: 'lg-flodb__railtitle', text: 'Narrow' }),
        active > 0
          ? el('button', {
              class: 'lg-link',
              text: `Clear ${active}`,
              attrs: { type: 'button' },
              on: {
                click: () => {
                  this.searchInput.value = '';
                  this.setFilter(defaultFloraFilter());
                },
              },
            })
          : null,
      ),
    );

    // Form factor first, because it is the axis a designer thinks in before any other.
    this.railEl.appendChild(
      this.facetRow(
        'Form',
        FORM_FACTORS.filter((form) => all.some((d) => formFactorOf(d) === form)).map((form) =>
          this.chip(
            formFactorLabel(form),
            this.filter.formFactors.includes(form),
            all.filter((d) => formFactorOf(d) === form).length,
            () =>
              this.setFilter({ formFactors: toggleFacet<FormFactor>(this.filter.formFactors, form) }),
          ),
        ),
      ),
    );

    if (this.isLocalized) {
      this.railEl.appendChild(
        this.facetRow('This site', [
          ...(['ideal', 'suitable', 'marginal', 'outside-range'] as const).map((rating) =>
            this.chip(
              suitabilityBadge(rating),
              this.filter.suitability.includes(rating),
              this.allRows().filter((row) => row.rating === rating).length,
              () => this.setFilter({ suitability: toggleFacet(this.filter.suitability, rating) }),
            ),
          ),
          this.chip('Native here', this.filter.nativeOnly, null, () =>
            this.setFilter({ nativeOnly: !this.filter.nativeOnly }),
          ),
        ]),
      );
    }

    this.railEl.appendChild(
      this.facetRow(
        'Colour through the year',
        COLOR_FAMILIES.filter((family) => all.some((d) => colorFamiliesOf(d).includes(family))).map(
          (family) =>
            el(
              'button',
              {
                class: `lg-swatch${this.filter.colorFamilies.includes(family) ? ' is-active' : ''}`,
                title: colorFamilyLabel(family),
                attrs: { type: 'button', 'aria-label': colorFamilyLabel(family) },
                on: {
                  click: () =>
                    this.setFilter({
                      colorFamilies: toggleFacet<ColorFamily>(this.filter.colorFamilies, family),
                    }),
                },
              },
              el('span', {
                class: 'lg-swatch__dot',
                style: { background: COLOR_FAMILY_SWATCHES[family] },
              }),
            ),
        ),
      ),
    );

    this.railEl.appendChild(
      this.facetRow(
        'Traits',
        tagFacets(all)
          .slice(0, this.allFacets ? 40 : 10)
          .map((facet) =>
            this.chip(facet.label, this.filter.tags.includes(facet.value), facet.count, () =>
              this.setFilter({ tags: toggleFacet(this.filter.tags, facet.value) }),
            ),
          ),
      ),
    );

    if (this.allFacets) {
      this.railEl.appendChild(
        this.facetRow(
          'Category',
          FLORA_CATEGORIES.filter((category) => all.some((d) => d.category === category)).map(
            (category) =>
              this.chip(
                categoryLabel(category),
                this.filter.categories.includes(category),
                all.filter((d) => d.category === category).length,
                () => this.setFilter({ categories: toggleFacet(this.filter.categories, category) }),
              ),
          ),
        ),
      );
      this.railEl.appendChild(
        this.facetRow(
          'Sun',
          (['full-sun', 'part-sun', 'part-shade', 'full-shade'] as const).map((sun) =>
            this.chip(sun.replace(/-/g, ' '), this.filter.sunlight.includes(sun), null, () =>
              this.setFilter({ sunlight: toggleFacet(this.filter.sunlight, sun) }),
            ),
          ),
        ),
      );
      this.railEl.appendChild(
        this.facetRow(
          'Moisture',
          (['dry', 'average', 'moist', 'wet'] as const).map((moisture) =>
            this.chip(moisture, this.filter.moisture.includes(moisture), null, () =>
              this.setFilter({ moisture: toggleFacet(this.filter.moisture, moisture) }),
            ),
          ),
        ),
      );
      this.railEl.appendChild(
        this.facetRow(
          'Genus',
          genusFacets(all).map((facet) =>
            this.chip(facet.label, this.filter.genera.includes(facet.value), facet.count, () =>
              this.setFilter({ genera: toggleFacet(this.filter.genera, facet.value) }),
            ),
          ),
        ),
      );
    }

    this.railEl.appendChild(
      this.facetRow('Shortlist', [
        this.chip('Favourites only', this.filter.favoritesOnly, null, () =>
          this.setFilter({ favoritesOnly: !this.filter.favoritesOnly }),
        ),
      ]),
    );

    this.railEl.appendChild(
      el('button', {
        class: 'lg-link lg-flodb__more',
        text: this.allFacets ? 'Fewer filters' : 'More filters',
        attrs: { type: 'button' },
        on: {
          click: () => {
            this.allFacets = !this.allFacets;
            this.render();
          },
        },
      }),
    );

    // A count that is not the whole catalog is worth restating at the bottom of the rail, where the
    // eye lands after working down it.
    this.railEl.appendChild(
      el('p', {
        class: 'lg-flodb__railnote',
        text: `${rows.length} plant${rows.length === 1 ? '' : 's'} in view.`,
      }),
    );
  }

  private facetRow(label: string, children: Child[]): HTMLElement {
    return el(
      'div',
      { class: 'lg-facet lg-flodb__facet' },
      el('span', { class: 'lg-facet__label', text: label }),
      el('div', { class: 'lg-facet__values' }, ...children),
    );
  }

  private chip(label: string, active: boolean, count: number | null, onClick: () => void): HTMLElement {
    return el(
      'button',
      {
        class: `lg-chip${active ? ' is-active' : ''}`,
        attrs: { type: 'button', 'aria-pressed': active },
        on: { click: onClick },
      },
      label,
      count !== null && el('span', { class: 'lg-chip__count', text: String(count) }),
    );
  }

  /* ------------------------------------ The stage ----------------------------------- */

  private renderStage(rows?: readonly DatabaseRow[]): void {
    const data = rows ?? this.rows();
    // Focusing a row rebuilds the stage, and a rebuild that scrolled back to the top would put the
    // row you just clicked off the screen. Held and restored rather than patched in place, because
    // every lens draws focus differently and only one of them is a list.
    const scroll = this.stageEl.scrollTop;
    clear(this.stageEl);

    if (data.length === 0 && this.lens !== 'compare') {
      this.stageEl.appendChild(
        el(
          'div',
          { class: 'lg-flodb__empty' },
          el('span', { class: 'lg-flodb__emptyicon', html: icon('plant', 24) }),
          el('p', { text: 'Nothing in the catalog matches this query.' }),
          el('button', {
            class: 'lg-link',
            text: 'Clear the filters',
            attrs: { type: 'button' },
            on: {
              click: () => {
                this.searchInput.value = '';
                this.setFilter(defaultFloraFilter());
              },
            },
          }),
        ),
      );
      return;
    }

    switch (this.lens) {
      case 'gallery':
        this.stageEl.appendChild(this.galleryLens(data));
        break;
      case 'table':
        this.stageEl.appendChild(this.tableLens(data));
        break;
      case 'year':
        this.stageEl.appendChild(this.yearLens(data));
        break;
      case 'site':
        this.stageEl.appendChild(this.siteLens(data));
        break;
      case 'wildlife':
        this.stageEl.appendChild(this.wildlifeLens(data));
        break;
      case 'compare':
        this.stageEl.appendChild(this.compareLens());
        break;
    }
    this.stageEl.scrollTop = scroll;
  }

  /* ------------------------------------- Gallery ------------------------------------ */

  private galleryLens(rows: readonly DatabaseRow[]): HTMLElement {
    const grid = el('div', { class: 'lg-flodb__gallery' });
    for (const row of rows) grid.appendChild(this.plate(row));
    return grid;
  }

  /**
   * One plant as a plate: the silhouette, its year of colour, and the two figures that decide
   * whether it is even a candidate — how big it gets here, and whether it belongs here.
   *
   * Pressing it picks the plant up — see {@link choose}.
   */
  private plate(row: DatabaseRow): HTMLElement {
    const facets = monthFacets(row);
    const pinIndex = this.pinned.indexOf(row.id);
    const armed = this.armedId === row.id;

    const plate = el(
      'article',
      {
        class: `lg-flodb__plate${armed ? ' is-active' : ''}${
          this.focusId === row.id ? ' is-focused' : ''
        }`,
        title: armed
          ? `${row.name} is in hand — close the database and click the ground to plant it`
          : `Pick up ${row.name} — it arms the plant tool and lights up in the palette`,
        attrs: {
          'data-flora-row': row.id,
          role: 'button',
          tabindex: '0',
          'aria-pressed': String(armed),
        },
        on: {
          click: () => this.choose(row.id),
          keydown: (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            this.choose(row.id);
          },
        },
      },
      el('div', {
        class: 'lg-flodb__platethumb',
        html: floraThumbnailSvg(row.definition, { season: this.deps.season() }),
      }),
      el(
        'div',
        { class: 'lg-flodb__platebody' },
        el('span', { class: 'lg-flodb__platename', text: row.name }),
        el('span', { class: 'lg-flodb__platelatin', text: row.botanicalName }),
        el('div', {
          class: 'lg-flodb__ribbon',
          html: yearRibbonSvg(facets),
          title: 'The plant’s year — foliage, bloom and fruit in its own recorded colours',
        }),
        el(
          'div',
          { class: 'lg-flodb__platefacts' },
          el('span', {
            text: `${formatLength(row.heightMeters, this.unit)} × ${formatLength(row.spreadMeters, this.unit)}`,
          }),
          row.described
            ? el('span', {
                class: 'lg-flodb__ecodot',
                title: `Ecological value ${percentLabel(row.ecologyScore)}`,
                style: { opacity: String(0.25 + row.ecologyScore * 0.75) },
              })
            : null,
        ),
      ),
      this.ratingChip(row),
      el(
        'div',
        { class: 'lg-flodb__plateactions' },
        this.plateButton(
          icon('info', 13),
          'Everything on record',
          (button) => this.card.toggle(row.definition, button),
          this.card.openId === row.id,
          row.id,
        ),
        this.plateButton(
          this.deps.preferences.isFavorite(row.id) ? filledIcon('star', 13) : icon('star', 13),
          'Favourite',
          () => {
            this.deps.preferences.toggleFavorite(row.id);
            this.renderStage();
          },
          this.deps.preferences.isFavorite(row.id),
        ),
        this.plateButton(
          icon('compare', 13),
          pinIndex >= 0 ? 'Unpin from Compare' : 'Pin to Compare',
          () => this.togglePin(row.id),
          pinIndex >= 0,
        ),
      ),
    );
    return plate;
  }

  private plateButton(
    markup: string,
    title: string,
    onClick: (button: HTMLElement) => void,
    active = false,
    infoFor?: string,
  ): HTMLElement {
    const button = el('button', {
      class: `lg-flodb__plateaction${active ? ' is-active' : ''}`,
      html: markup,
      title,
      attrs: {
        type: 'button',
        'aria-label': title,
        ...(infoFor === undefined ? {} : { 'data-flora-info': infoFor }),
      },
    });
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      onClick(button);
    });
    return button;
  }

  /**
   * The site verdict, as a word.
   *
   * Only marginal, out-of-range and locally native plants get one — the same rule the palette tile
   * follows, for the same reason: a badge on every plate would be noise on a wall where most plants
   * are simply fine, and "suitable" is the answer you get by not being warned.
   */
  private ratingChip(row: DatabaseRow): HTMLElement | null {
    if (!row.localized) return null;
    if (row.rating === 'marginal' || row.rating === 'outside-range') {
      const warning = primaryWarning(row.resolved.suitability);
      return el('span', {
        class: `lg-flodb__fit lg-flodb__fit--${row.rating === 'marginal' ? 'warn' : 'bad'}`,
        text: suitabilityBadge(row.rating),
        title: warning ? `${suitabilityLabel(row.rating)} — ${warning.message}` : suitabilityLabel(row.rating),
      });
    }
    if (row.nativeStatus === 'native') {
      return el('span', {
        class: 'lg-flodb__fit lg-flodb__fit--native',
        text: 'Native',
        title: 'Indigenous to this site’s region.',
      });
    }
    return null;
  }

  /* -------------------------------------- Table ------------------------------------- */

  private tableLens(rows: readonly DatabaseRow[]): HTMLElement {
    const columns = columnsFor(this.groupsOn);
    if (columns.length === 0) {
      return el('p', { class: 'lg-flodb__hint', text: 'Switch a column group on to see the records.' });
    }

    const head = el('tr');
    head.appendChild(el('th', { class: 'lg-flodb__gutter', attrs: { scope: 'col' } }));
    for (const column of columns) {
      const sorted = this.sortColumn === column.id;
      head.appendChild(
        el(
          'th',
          {
            class: `${column.numeric ? 'is-numeric ' : ''}${sorted ? 'is-sorted ' : ''}lg-flodb__th`,
            attrs: {
              scope: 'col',
              'aria-sort': sorted ? this.sortDirection : 'none',
              ...(column.hint ? { title: column.hint } : {}),
            },
            on: {
              click: () => {
                if (this.sortColumn === column.id) {
                  this.sortDirection = this.sortDirection === 'ascending' ? 'descending' : 'ascending';
                } else {
                  this.sortColumn = column.id;
                  this.sortDirection = 'ascending';
                }
                this.render();
              },
            },
          },
          el('span', { text: column.label }),
          sorted
            ? el('span', {
                class: 'lg-flodb__sortmark',
                text: this.sortDirection === 'ascending' ? '▲' : '▼',
              })
            : null,
        ),
      );
    }

    const shown = rows.slice(0, this.tableLimit);
    const body = el('tbody');
    for (const row of shown) body.appendChild(this.tableRow(row, columns));

    const table = el(
      'table',
      { class: 'lg-flodb__table' },
      el('thead', {}, head),
      body,
    );

    if (rows.length > shown.length) {
      return el(
        'div',
        { class: 'lg-flodb__paged' },
        table,
        el('button', {
          class: 'lg-flodb__more',
          text: `Show ${Math.min(TABLE_PAGE_SIZE, rows.length - shown.length)} more — ${shown.length} of ${rows.length} listed`,
          attrs: { type: 'button' },
          on: {
            click: () => {
              this.tableLimit += TABLE_PAGE_SIZE;
              this.renderStage();
            },
          },
        }),
      );
    }
    return table;
  }

  private tableRow(row: DatabaseRow, columns: readonly TableColumn[]): HTMLElement {
    const tr = el('tr', {
      class: `lg-flodb__tr${this.focusId === row.id ? ' is-focused' : ''}`,
      attrs: { tabindex: '0', 'data-flora-row': row.id },
      on: {
        click: () => this.setFocus(row.id),
        keydown: (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.setFocus(row.id);
        },
      },
    });

    const info = el('button', {
      class: 'lg-flodb__rowinfo',
      html: icon('info', 13),
      title: `Everything on record for ${row.name}`,
      attrs: { type: 'button', 'aria-label': `Plant record for ${row.name}`, 'data-flora-info': row.id },
    });
    info.addEventListener('click', (event) => {
      event.stopPropagation();
      this.card.toggle(row.definition, info);
    });
    tr.appendChild(el('td', { class: 'lg-flodb__gutter' }, info));

    for (const column of columns) {
      const absent = column.absent?.(row) === true;
      const cell = el('td', {
        class: `${column.numeric ? 'is-numeric' : ''}${absent ? ' is-absent' : ''}`,
      });
      cell.appendChild(el('span', { text: column.value(row, this.unit) }));
      // A parameter can be argued with wherever it is shown — that is the point of raising the
      // suggestion from the cell rather than from a form that asks which field you meant.
      if (column.field !== null) {
        // A pencil, not a warning triangle. The flag says "this can be argued with", and a row of
        // amber triangles says "these figures are wrong" about a table nobody has questioned.
        const flag = el('button', {
          class: 'lg-flodb__suggest',
          html: icon('rename', 11),
          title: `Suggest a value for ${column.label}`,
          attrs: { type: 'button', 'aria-label': `Suggest a value for ${column.label} on ${row.name}` },
        });
        flag.addEventListener('click', (event) => {
          event.stopPropagation();
          this.openFeedback({
            scope: 'parameter',
            floraId: row.id,
            floraName: row.name,
            field: column.field ?? '',
            fieldLabel: column.label,
            currentValue: column.value(row, this.unit),
            message: '',
          });
        });
        cell.appendChild(flag);
      }
      tr.appendChild(cell);
    }
    return tr;
  }

  /* --------------------------------------- Year ------------------------------------- */

  /**
   * The seasonality lens: every plant's year, and above it the year the whole selection makes.
   *
   * The river at the top is the answer to the question a planting plan is really asking — *what will
   * this look like in May* — and it is the one drawing here that could not be read off any single
   * record, because it is a property of the shortlist rather than of a plant.
   */
  private yearLens(rows: readonly DatabaseRow[]): HTMLElement {
    const river = paletteRiver(rows);
    const stage = el('div', { class: 'lg-flodb__year' });

    stage.appendChild(
      el(
        'section',
        { class: 'lg-flodb__block' },
        el('h3', { class: 'lg-flodb__blocktitle', text: 'The year this palette makes' }),
        el('p', {
          class: 'lg-flodb__blocknote',
          text:
            'Each month, the colours these plants are carrying — foliage, flower and fruit, in the ' +
            `hues the records actually store. Bloom and fruit count ${'×'}4 against foliage: a hundred ` +
            'green leaves and one flower is not a half-pink month, and it does not look like one either.',
        }),
        el('div', { class: 'lg-flodb__figure', html: riverSvg(river) }),
        this.monthAxis(),
      ),
    );

    const list = el('div', { class: 'lg-flodb__years' });
    list.appendChild(
      el(
        'div',
        { class: 'lg-flodb__yearhead' },
        el('span', { text: this.isLocalized ? `Dated for ${this.localization.regionLabel}` : 'Reference climate' }),
        el('div', { class: 'lg-flodb__yearmonths' }, ...MONTH_LABELS.map((month) =>
          el('span', { class: 'lg-flodb__yearmonth', text: month.slice(0, 1) }),
        )),
      ),
    );

    for (const row of rows) {
      const facets = monthFacets(row);
      const line = el(
        'div',
        {
          class: `lg-flodb__yearrow${this.focusId === row.id ? ' is-focused' : ''}`,
          attrs: { 'data-flora-row': row.id },
          on: { click: () => this.setFocus(row.id) },
        },
        el(
          'span',
          { class: 'lg-flodb__yearname' },
          el('span', { text: row.name }),
          el('span', { class: 'lg-flodb__yearlatin', text: row.botanicalName }),
        ),
        this.yearBand(facets),
      );
      list.appendChild(line);
    }

    stage.appendChild(
      el(
        'section',
        { class: 'lg-flodb__block' },
        el('h3', { class: 'lg-flodb__blocktitle', text: 'Plant by plant' }),
        el('p', {
          class: 'lg-flodb__blocknote',
          text:
            'The wide band is foliage, fading as the plant goes bare; the inner band is bloom; the ' +
            'dots are fruit. Sampled mid-month through the same appearance model the canvas runs.',
        }),
        list,
        el(
          'div',
          { class: 'lg-flodb__legend' },
          legendItem('Foliage', 'linear-gradient(90deg, #7aa05a, #3f7a35)'),
          legendItem('Bloom', 'repeating-linear-gradient(90deg, #d9a12c 0 4px, #d1749c 4px 8px)'),
          legendItem('Fruit', 'radial-gradient(circle, #c0433f 45%, transparent 46%)'),
        ),
      ),
    );

    return stage;
  }

  /** The stretched bars, with the round fruit markers layered over them. See {@link yearDots}. */
  private yearBand(facets: ReturnType<typeof monthFacets>): HTMLElement {
    return el(
      'div',
      { class: 'lg-flodb__yearband' },
      el('div', { class: 'lg-flodb__yearbars', html: yearBandSvg(facets) }),
      yearDots(facets, (index) => MONTH_LABELS[index] ?? ''),
    );
  }

  private monthAxis(): HTMLElement {
    const axis = el('div', { class: 'lg-flodb__axis' });
    for (const month of MONTH_LABELS) {
      axis.appendChild(el('span', { class: 'lg-flodb__axistick', text: month.slice(0, 1) }));
    }
    return axis;
  }

  /* --------------------------------------- Site ------------------------------------- */

  private siteLens(rows: readonly DatabaseRow[]): HTMLElement {
    const stage = el('div', { class: 'lg-flodb__site-lens' });

    if (!this.isLocalized) {
      stage.appendChild(
        el(
          'div',
          { class: 'lg-flodb__prompt' },
          el('span', { class: 'lg-flodb__prompticon', html: icon('pin', 16) }),
          el('p', {
            text:
              'This site has no location. The ranges below are the ones the records claim; nothing ' +
              'is being compared against a climate. Type a ZIP code in the bar above the flora ' +
              'palette and every figure here becomes an answer about your garden.',
          }),
        ),
      );
    }

    const bands = suitabilityBands(rows);
    if (this.isLocalized) {
      stage.appendChild(
        el(
          'section',
          { class: 'lg-flodb__block' },
          el('h3', { class: 'lg-flodb__blocktitle', text: 'How this selection reads here' }),
          el('div', { class: 'lg-flodb__stack' }, ...bands
            .filter((band) => band.count > 0)
            .map((band) =>
              el('button', {
                class: `lg-flodb__stackpart is-${band.tone}${
                  this.filter.suitability.length === 1 && this.filter.suitability[0] === band.rating
                    ? ' is-active'
                    : ''
                }`,
                style: { flexGrow: String(Math.max(band.count, 0.0001)) },
                title: `${band.count} ${band.label} — click to show only these`,
                attrs: { type: 'button' },
                on: {
                  click: () =>
                    this.setFilter({
                      suitability:
                        this.filter.suitability.length === 1 &&
                        this.filter.suitability[0] === band.rating
                          ? []
                          : [band.rating],
                    }),
                },
              }),
            ),
          ),
          el('div', { class: 'lg-flodb__legend' }, ...bands
            .filter((band) => band.count > 0)
            .map((band) =>
              el(
                'span',
                { class: 'lg-flodb__legenditem' },
                el('span', { class: `lg-flodb__legendswatch is-${band.tone}` }),
                el('span', { text: `${band.count} ${band.label.toLowerCase()}` }),
              ),
            ),
          ),
        ),
      );
    }

    /* The zone ribbon — the drawing that answers "how much headroom does this have". */
    const ribbon = el('div', { class: 'lg-flodb__ribbons' });
    const siteZone = this.isLocalized
      ? this.localization.hardinessZone.number + (this.localization.hardinessZone.band === 'b' ? 0.5 : 0)
      : null;
    const ordered = [...rows].sort(
      (a, b) => (zoneSpanOf(a.definition)?.minimum ?? 99) - (zoneSpanOf(b.definition)?.minimum ?? 99),
    );
    for (const row of ordered) {
      const span = zoneSpanOf(row.definition);
      ribbon.appendChild(
        el(
          'div',
          {
            class: `lg-flodb__ribbonrow${this.focusId === row.id ? ' is-focused' : ''}`,
            attrs: { 'data-flora-row': row.id },
            on: { click: () => this.setFocus(row.id) },
          },
          el('span', { class: 'lg-flodb__ribbonname', text: row.name }),
          el('div', {
            class: 'lg-flodb__ribbonbar',
            html: zoneBarSvg(span, siteZone, row.rating, row.localized),
            title:
              span === null
                ? 'This record states no hardiness range.'
                : `Zones ${span.minimum} – ${span.maximum}${
                    siteZone === null ? '' : ` · this site is ${siteZone}`
                  }`,
          }),
        ),
      );
    }

    stage.appendChild(
      el(
        'section',
        { class: 'lg-flodb__block' },
        el('h3', { class: 'lg-flodb__blocktitle', text: 'Hardiness range against this site' }),
        el('p', {
          class: 'lg-flodb__blocknote',
          text: siteZone
            ? `The line is this site, zone ${zoneLabel(this.localization.hardinessZone)}. A bar that does not ` +
              'reach it is a plant asking for a conversation, not a plant the app will refuse to place.'
            : 'Each bar is the span of winters the record claims. Localize the site to see where it falls.',
        }),
        el('div', { class: 'lg-flodb__zoneaxis' }, ...zoneTicks()),
        ribbon,
      ),
    );

    stage.appendChild(
      el(
        'section',
        { class: 'lg-flodb__block' },
        el('h3', { class: 'lg-flodb__blocktitle', text: 'Expected size here' }),
        el('p', {
          class: 'lg-flodb__blocknote',
          text:
            'Height against spread, at the size this climate is expected to grow them to — a point ' +
            'inside each record’s own range, never a number the botany does not claim. Shape carries ' +
            'the form factor.',
        }),
        el('div', {
          class: 'lg-flodb__figure lg-flodb__figure--plot',
          html: scatterSvg(scalePoints(rows), this.unit, this.focusId),
        }),
        el(
          'div',
          { class: 'lg-flodb__legend' },
          ...FORM_FACTORS.map((form) =>
            el(
              'span',
              { class: 'lg-flodb__legenditem' },
              el('span', { class: 'lg-flodb__legendglyph', html: formGlyphSvg(form) }),
              el('span', { text: formFactorLabel(form) }),
            ),
          ),
        ),
      ),
    );

    return stage;
  }

  /* ------------------------------------- Wildlife ----------------------------------- */

  private wildlifeLens(rows: readonly DatabaseRow[]): HTMLElement {
    const rollup = ecologyRollup(rows, this.localization);
    const stage = el('div', { class: 'lg-flodb__wildlife' });

    stage.appendChild(
      el('p', {
        class: 'lg-flodb__hint',
        text:
          `${rollup.described} of ${rollup.total} plants in view carry ecological data. Months are ` +
          (this.isLocalized
            ? `derived from each plant’s own bloom and fruiting in ${this.localization.regionLabel}.`
            : 'derived against the catalog’s reference climate.') +
          ' Bands read “one of each planted”, which is what a shortlist is, and each row is shaded ' +
          'against its own strongest month — so a row shows you when, and the count beside it shows ' +
          'you how many plants are behind it.',
      }),
    );

    if (rollup.food.length > 0) {
      stage.appendChild(this.calendarBlock('Food through the year', rollup.food));
    }
    if (rollup.habitat.length > 0) {
      stage.appendChild(this.calendarBlock('Habitat through the year', rollup.habitat));
    }

    if (rollup.groups.length > 0) {
      const list = el('div', { class: 'lg-flodb__groups' });
      for (const tally of rollup.groups) {
        list.appendChild(
          el(
            'div',
            { class: 'lg-flodb__group' },
            el(
              'div',
              { class: 'lg-flodb__grouphead' },
              el('span', { class: 'lg-flodb__groupname', text: tally.label }),
              el('span', { class: 'lg-flodb__groupcount', text: `${tally.count}` }),
            ),
            el(
              'span',
              { class: 'lg-flodb__meter' },
              el('span', {
                class: 'lg-flodb__meterfill',
                style: { width: `${Math.round(tally.share * 100)}%` },
              }),
            ),
            el('span', {
              class: 'lg-flodb__grouptop',
              text: tally.top.map((row) => row.name).join(' · '),
            }),
          ),
        );
      }
      stage.appendChild(
        el(
          'section',
          { class: 'lg-flodb__block' },
          el('h3', { class: 'lg-flodb__blocktitle', text: 'Who turns up' }),
          el('p', {
            class: 'lg-flodb__blocknote',
            text: 'Plants in view offering each group anything at all, with the strongest three named.',
          }),
          list,
        ),
      );
    }

    /* Larval hosts are separated from everything above deliberately — see `FloraEcology.ts`. */
    if (rollup.hosts.length > 0 || rollup.specialists.length > 0) {
      const hosts = el('div', { class: 'lg-flodb__hosts' });
      for (const entry of [...rollup.hosts, ...rollup.specialists]) {
        hosts.appendChild(
          el(
            'div',
            {
              class: 'lg-flodb__host',
              ...(entry.note ? { title: entry.note } : {}),
              on: { click: () => this.setFocus(entry.row.id) },
            },
            el('span', { class: 'lg-flodb__hostname', text: entry.row.name }),
            el('span', { class: 'lg-flodb__hostgroup', text: entry.label }),
            el('span', {
              class: 'lg-flodb__hostcount',
              text: entry.speciesCount === null ? 'documented' : `${entry.speciesCount} species`,
            }),
          ),
        );
      }
      stage.appendChild(
        el(
          'section',
          { class: 'lg-flodb__block' },
          el('h3', { class: 'lg-flodb__blocktitle', text: 'Hosts and specialists' }),
          el('p', {
            class: 'lg-flodb__blocknote',
            text:
              'Nectar feeds adult butterflies; only host foliage produces the next generation, and a ' +
              'specialist bee cannot substitute one plant for another. These are the relationships ' +
              'nothing else in the list can stand in for.',
          }),
          hosts,
        ),
      );
    }

    const ranked = rollup.ranked.filter((row) => row.described).slice(0, 20);
    if (ranked.length > 0) {
      const bars = el('div', { class: 'lg-flodb__ranked' });
      for (const row of ranked) {
        bars.appendChild(
          el(
            'div',
            {
              class: `lg-flodb__rank${this.focusId === row.id ? ' is-focused' : ''}`,
              on: { click: () => this.setFocus(row.id) },
            },
            el('span', { class: 'lg-flodb__rankname', text: row.name }),
            el(
              'span',
              { class: 'lg-flodb__meter' },
              el('span', {
                class: 'lg-flodb__meterfill',
                style: { width: `${Math.round(row.ecologyScore * 100)}%` },
              }),
            ),
            el('span', { class: 'lg-flodb__rankvalue', text: percentLabel(row.ecologyScore) }),
            row.resolved.ecology.keystone
              ? el('span', {
                  class: 'lg-flodb__keystone',
                  text: 'keystone',
                  title: 'Carries a disproportionate share of the local insect food web',
                })
              : null,
          ),
        );
      }
      stage.appendChild(
        el(
          'section',
          { class: 'lg-flodb__block' },
          el('h3', { class: 'lg-flodb__blocktitle', text: 'Ecological value' }),
          el('p', {
            class: 'lg-flodb__blocknote',
            text:
              'A blunt figure, for ranking rather than for citing: food weighted over habitat, hosting ' +
              'and specialist relationships over both.',
          }),
          bars,
        ),
      );
    }

    /* What the catalog does not yet know — a finding in its own right, and where a suggestion helps. */
    const unrecorded = rollup.morphology.find((entry) => entry.morphology === null)?.count ?? 0;
    stage.appendChild(
      el(
        'section',
        { class: 'lg-flodb__block lg-flodb__block--gap' },
        el('h3', { class: 'lg-flodb__blocktitle', text: 'What the catalog does not know yet' }),
        el('p', {
          class: 'lg-flodb__blocknote',
          text:
            `Flower shape decides which pollinators can reach a plant's nectar at all, and ${unrecorded} of ` +
            `${rollup.total} records in view do not state it. The field exists; the observations do not. ` +
            'If you know one, say so.',
        }),
        el(
          'div',
          { class: 'lg-flodb__coverage' },
          ...rollup.morphology.map((entry) =>
            el(
              'span',
              {
                class: `lg-flodb__coveragechip${entry.morphology === null ? ' is-absent' : ''}`,
              },
              el('strong', { text: String(entry.count) }),
              el('span', { text: entry.label }),
            ),
          ),
        ),
        el('button', {
          class: 'lg-link',
          text: 'Suggest flower shapes',
          attrs: { type: 'button' },
          on: {
            click: () =>
              this.openFeedback({
                scope: 'parameter',
                field: 'ecology.floralMorphology',
                fieldLabel: 'Flower shape',
                message: '',
              }),
          },
        }),
      ),
    );

    return stage;
  }

  private calendarBlock(
    title: string,
    calendarRows: readonly { label: string; months: number[]; contributors: number }[],
  ): HTMLElement {
    const grid = el('div', { class: 'lg-flodb__calendar' });
    grid.appendChild(el('span'));
    for (const month of MONTH_LABELS) {
      grid.appendChild(el('span', { class: 'lg-flodb__calhead', text: month.slice(0, 1) }));
    }
    for (const row of calendarRows) {
      grid.appendChild(
        el(
          'span',
          {
            class: 'lg-flodb__callabel',
            title: `${row.label} — from ${row.contributors} plant${row.contributors === 1 ? '' : 's'} in view`,
          },
          el('span', { class: 'lg-flodb__calname', text: row.label }),
          // The magnitude the per-row shading deliberately drops. Without it a row offered by one
          // plant and a row offered by forty would look identical.
          el('span', { class: 'lg-flodb__calcount', text: String(row.contributors) }),
        ),
      );
      // A resource the record calls `year-round` really is the same in every month, and twelve
      // identical cells is a chart encoding nothing. Say it once, across the year, in words.
      const constant =
        Math.max(...row.months) - Math.min(...row.months) < 0.02 && Math.max(...row.months) > 0.02;
      if (constant) {
        grid.appendChild(
          el('span', {
            class: 'lg-flodb__calcell lg-flodb__calcell--constant',
            text: 'Year-round',
            title: `${row.label} — present in every month, from ${row.contributors} plant${
              row.contributors === 1 ? '' : 's'
            } in view`,
          }),
        );
        continue;
      }
      row.months.forEach((value, index) => {
        grid.appendChild(
          el('span', {
            class: 'lg-flodb__calcell',
            title: `${row.label}, ${MONTH_LABELS[index]} — ${percentLabel(value)}`,
            // One hue at twelve strengths: the bands are one quantity, and a hue scale would imply
            // categories the data does not have.
            style: value < 0.02 ? {} : { background: `rgb(108 194 106 / ${Math.round(value * 88)}%)` },
          }),
        );
      });
    }
    return el(
      'section',
      { class: 'lg-flodb__block' },
      el('h3', { class: 'lg-flodb__blocktitle', text: title }),
      grid,
    );
  }

  /* -------------------------------------- Compare ----------------------------------- */

  private compareLens(): HTMLElement {
    // Built once and indexed, rather than searched per pin: the whole catalog is walked either way,
    // and doing it four times over is four times the resolver traffic for the same answer.
    const byId = new Map(this.allRows().map((row) => [row.id, row]));
    const pinnedRows = this.pinned
      .map((id) => byId.get(id))
      .filter((row): row is DatabaseRow => row !== undefined);

    if (pinnedRows.length === 0) {
      return el(
        'div',
        { class: 'lg-flodb__empty' },
        el('span', { class: 'lg-flodb__emptyicon', html: icon('compare', 24) }),
        el('p', {
          text:
            'Nothing pinned yet. Press the compare button on up to four plates and every parameter ' +
            'lines up side by side here.',
        }),
        el('button', {
          class: 'lg-link',
          text: 'Back to the gallery',
          attrs: { type: 'button' },
          on: { click: () => this.setLens('gallery') },
        }),
      );
    }

    const colors = COMPARE_COLORS[this.theme];
    const stage = el('div', { class: 'lg-flodb__compare' });

    /* The overlaid years first — a comparison you read rather than one you tabulate. */
    const strips = el('div', { class: 'lg-flodb__comparestrips' });
    pinnedRows.forEach((row, index) => {
      strips.appendChild(
        el(
          'div',
          { class: 'lg-flodb__comparestrip' },
          el(
            'span',
            { class: 'lg-flodb__comparelabel' },
            el('span', {
              class: 'lg-flodb__compareswatch',
              style: { background: colors[index] ?? 'var(--lg-accent)' },
            }),
            el('span', { text: row.name }),
          ),
          this.yearBand(monthFacets(row)),
        ),
      );
    });

    stage.appendChild(
      el(
        'section',
        { class: 'lg-flodb__block' },
        el('h3', { class: 'lg-flodb__blocktitle', text: 'Their years, one under the other' }),
        el('div', { class: 'lg-flodb__yearmonths lg-flodb__yearmonths--compare' }, ...MONTH_LABELS.map(
          (month) => el('span', { class: 'lg-flodb__yearmonth', text: month.slice(0, 1) }),
        )),
        strips,
      ),
    );

    const table = el('table', { class: 'lg-flodb__comparetable' });
    const head = el('tr');
    head.appendChild(el('th', { text: '', attrs: { scope: 'col' } }));
    pinnedRows.forEach((row, index) => {
      head.appendChild(
        el(
          'th',
          { attrs: { scope: 'col' } },
          el(
            'div',
            { class: 'lg-flodb__comparehead' },
            el('span', {
              class: 'lg-flodb__compareswatch',
              style: { background: colors[index] ?? 'var(--lg-accent)' },
            }),
            el('span', { class: 'lg-flodb__comparename', text: row.name }),
            el('button', {
              class: 'lg-flodb__unpin',
              html: icon('close', 11),
              title: `Unpin ${row.name}`,
              attrs: { type: 'button', 'aria-label': `Unpin ${row.name}` },
              on: { click: () => this.togglePin(row.id) },
            }),
          ),
          el('span', { class: 'lg-flodb__comparelatin', text: row.botanicalName }),
        ),
      );
    });
    table.appendChild(el('thead', {}, head));

    const body = el('tbody');
    for (const section of compareSections(pinnedRows, this.unit)) {
      body.appendChild(
        el(
          'tr',
          { class: 'lg-flodb__comparesection' },
          el(
            'th',
            { attrs: { colspan: String(pinnedRows.length + 1), scope: 'colgroup' } },
            el('span', { text: section.title }),
            el('span', { class: 'lg-flodb__comparenote', text: section.note }),
          ),
        ),
      );
      for (const row of section.rows) {
        const tr = el('tr', { class: row.differs ? 'lg-flodb__comparerow is-differing' : 'lg-flodb__comparerow' });
        tr.appendChild(
          el(
            'th',
            { class: 'lg-flodb__comparekey', attrs: { scope: 'row', ...(row.hint ? { title: row.hint } : {}) } },
            el('span', { text: row.label }),
          ),
        );
        for (const cell of row.cells) {
          const td = el('td', { class: cell.absent ? 'is-absent' : '' });
          if (cell.color) {
            td.appendChild(
              el('span', {
                class: 'lg-flodb__compareink',
                style: { background: safeColor(cell.color, '#888') },
              }),
            );
          }
          td.appendChild(el('span', { text: cell.value }));
          if (cell.meter !== null) {
            td.appendChild(
              el(
                'span',
                { class: 'lg-flodb__meter lg-flodb__meter--inline' },
                el('span', {
                  class: 'lg-flodb__meterfill',
                  style: { width: `${Math.round(Math.min(1, Math.max(0, cell.meter)) * 100)}%` },
                }),
              ),
            );
          }
          tr.appendChild(td);
        }
        body.appendChild(tr);
      }
    }
    table.appendChild(body);

    stage.appendChild(
      el(
        'section',
        { class: 'lg-flodb__block' },
        el('h3', { class: 'lg-flodb__blocktitle', text: 'Every parameter' }),
        el('p', {
          class: 'lg-flodb__blocknote',
          text:
            'Rows where the pinned plants disagree are marked. This is the record card’s own ' +
            'factsheet, turned on its side — so a figure here is the figure the card shows.',
        }),
        table,
      ),
    );

    return stage;
  }

  private get theme(): 'light' | 'dark' {
    if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  /* --------------------------------------- Pins ------------------------------------- */

  private renderPins(): void {
    clear(this.pinTray);
    if (this.pinned.length === 0) {
      this.pinTray.appendChild(
        el('span', { class: 'lg-flodb__pinhint', text: 'Pin plants to compare them' }),
      );
      return;
    }
    const colors = COMPARE_COLORS[this.theme];
    this.pinned.forEach((id, index) => {
      const definition = this.deps.catalog.find(id);
      if (!definition) return;
      this.pinTray.appendChild(
        el(
          'button',
          {
            class: 'lg-flodb__pin',
            title: 'Open Compare  ·  right-click to unpin',
            attrs: { type: 'button' },
            on: {
              click: () => this.setLens('compare'),
              contextmenu: (event) => {
                event.preventDefault();
                this.togglePin(id);
              },
            },
          },
          el('span', {
            class: 'lg-flodb__compareswatch',
            style: { background: colors[index] ?? 'var(--lg-accent)' },
          }),
          el('span', { text: definition.commonNames[0] ?? definition.botanicalName }),
        ),
      );
    });
    this.pinTray.appendChild(
      el('button', {
        class: 'lg-link',
        text: 'Clear',
        attrs: { type: 'button' },
        on: {
          click: () => {
            this.pinned = [];
            this.render();
          },
        },
      }),
    );
  }

  /* ------------------------------------- Feedback ----------------------------------- */

  /**
   * Raise a suggestion about whatever was pressed.
   *
   * The draft arrives already carrying its context — the plant, the field, the value on record — so
   * the form asks for the one thing only a person can supply. See `Feedback.ts` for why the transport
   * behind it is deliberately a stub.
   */
  private openFeedback(draft: FeedbackDraft): void {
    this.feedbackDialog?.dispose();

    const proposed = textInput({
      placeholder: draft.scope === 'parameter' ? 'The value you believe is right' : 'Plant or subject',
      ariaLabel: 'Suggested value',
      ...(draft.proposedValue ? { value: draft.proposedValue } : {}),
    });
    const message = el('textarea', {
      class: 'lg-input lg-flodb__textarea',
      attrs: {
        rows: '4',
        placeholder: feedbackPrompt(draft),
        spellcheck: 'true',
        'aria-label': 'What and why',
      },
    });
    const contact = textInput({
      placeholder: 'Email, if you would like a reply (optional)',
      ariaLabel: 'Contact',
    });
    const status = el('p', { class: 'lg-legend lg-flodb__status' });

    const dialog = new Dialog({
      host: this.root,
      title: feedbackTitle(draft),
      subtitle: draft.floraName ?? 'Catalog suggestion',
      icon: 'rename',
      onClose: () => {
        this.feedbackDialog = null;
      },
    });
    this.feedbackDialog = dialog;

    if (draft.currentValue !== undefined) {
      dialog.body.appendChild(
        dialogField(
          'On record',
          el('p', { class: 'lg-flodb__onrecord', text: draft.currentValue }),
          ...(draft.field ? [`Field: ${draft.field}`] : []),
        ),
      );
    }
    dialog.body.appendChild(dialogField('Suggested value', proposed));
    dialog.body.appendChild(dialogField('What and why', message));
    dialog.body.appendChild(dialogField('Contact', contact));
    dialog.body.appendChild(el('p', { class: 'lg-hint', text: this.channel.describe }));
    dialog.body.appendChild(status);

    dialog.setActions(
      dialogButton({ label: 'Cancel', onClick: () => dialog.close() }),
      dialogButton({
        label: 'Send suggestion',
        primary: true,
        onClick: () => {
          if (message.value.trim() === '') {
            status.textContent = 'Say what is wrong — that is the part nobody else can supply.';
            status.classList.add('is-error');
            message.focus();
            return;
          }
          void submitFeedback(
            this.channel,
            {
              ...draft,
              message: message.value.trim(),
              ...(proposed.value.trim() ? { proposedValue: proposed.value.trim() } : {}),
              ...(contact.value.trim() ? { contact: contact.value.trim() } : {}),
            },
            {
              catalogVersion: this.deps.catalog.version ?? 'unknown',
              appVersion: this.deps.appVersion ?? 'dev',
            },
          ).then((result) => {
            dialog.close();
            this.flashNote(result.message);
          });
        },
      }),
    );

    dialog.show();
    message.focus();
  }

  /** Say something in the footer, then let the standing summary take the line back. */
  private flashNote(text: string): void {
    this.noteEl.textContent = text;
    setTimeout(() => {
      if (this.noteEl.textContent === text) this.noteEl.textContent = '';
    }, 3600);
  }

  private downloadCsv(): void {
    const csv = databaseCsv(this.rows(), columnsFor(this.groupsOn), this.unit);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = el('a', {
      attrs: { href: url, download: 'flora-database.csv' },
      style: { display: 'none' },
    });
    this.root.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    this.flashNote('Exported flora-database.csv');
  }

  /** Light the ⓘ that owns the open card, without re-rendering the row it sits in. */
  private markCardActive(): void {
    for (const button of this.stageEl.querySelectorAll<HTMLElement>('[data-flora-info]')) {
      setClass(button, 'is-active', button.dataset.floraInfo === this.card.openId);
    }
  }

  dispose(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.onKeyDown, true);
    }
    this.feedbackDialog?.dispose();
    this.dateScrubber?.dispose();
    this.card.dispose();
    this.root.remove();
  }
}

/* ======================================== Figures ======================================== */

/*
 * Plain SVG strings, the way `FloraCard` draws its figures: each is a pure function of data this
 * module already computed, rebuilt whole on every render, and readable in one place. Every colour
 * interpolated below is either a token, a status class, or a botanical hex put through `safeColor`.
 */

/** A plate's year in 12 cells — foliage as ground, bloom over it, fruit as a dot. */
function yearRibbonSvg(facets: readonly { foliageColor: string; foliageDensity: number; flowerColor: string | null; flowerDensity: number; fruitColor: string | null; fruitDensity: number }[]): string {
  const width = 144;
  const height = 14;
  const cell = width / 12;
  const parts = facets.map((facet, index) => {
    const x = index * cell;
    const foliage =
      `<rect x="${x.toFixed(1)}" y="0" width="${(cell - 1).toFixed(1)}" height="${height}" rx="2" ` +
      `fill="${safeColor(facet.foliageColor, '#6b7482')}" opacity="${(0.12 + facet.foliageDensity * 0.8).toFixed(2)}"/>`;
    const bloom = facet.flowerColor
      ? `<rect x="${x.toFixed(1)}" y="${(height * 0.28).toFixed(1)}" width="${(cell - 1).toFixed(1)}" ` +
        `height="${(height * 0.44).toFixed(1)}" rx="1.5" fill="${safeColor(facet.flowerColor, '#c9a227')}" ` +
        `opacity="${Math.min(1, 0.45 + facet.flowerDensity).toFixed(2)}"/>`
      : '';
    const fruit = facet.fruitColor
      ? `<circle cx="${(x + (cell - 1) / 2).toFixed(1)}" cy="${(height / 2).toFixed(1)}" r="2.2" ` +
        `fill="${safeColor(facet.fruitColor, '#c0433f')}" opacity="${Math.min(1, 0.5 + facet.fruitDensity).toFixed(2)}"/>`
      : '';
    return foliage + bloom + fruit;
  });
  return (
    `<svg class="lg-flodb__svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" role="img" ` +
    `aria-label="Colour through the year">${parts.join('')}</svg>`
  );
}

/** The Year lens's wide band — the same encoding as the plate ribbon, drawn to be read across. */
function yearBandSvg(
  facets: readonly {
    foliageColor: string;
    foliageDensity: number;
    flowerColor: string | null;
    flowerDensity: number;
    fruitColor: string | null;
    fruitDensity: number;
    dormant: boolean;
  }[],
): string {
  const width = 360;
  const height = 22;
  const cell = width / 12;
  const parts = facets.map((facet, index) => {
    const x = index * cell;
    // A bare month is a hairline at the baseline rather than a gap: the plant is still there, and a
    // blank cell reads as missing data.
    const bare =
      `<rect x="${x.toFixed(1)}" y="${(height - 2).toFixed(1)}" width="${(cell - 2).toFixed(1)}" ` +
      `height="1.5" rx="0.75" fill="currentColor" opacity="0.22"/>`;
    const foliage =
      facet.foliageDensity > 0.02
        ? `<rect x="${x.toFixed(1)}" y="${(height * (1 - facet.foliageDensity) * 0.55).toFixed(1)}" ` +
          `width="${(cell - 2).toFixed(1)}" height="${(height - height * (1 - facet.foliageDensity) * 0.55).toFixed(1)}" ` +
          `rx="2" fill="${safeColor(facet.foliageColor, '#6b7482')}" opacity="${(0.3 + facet.foliageDensity * 0.65).toFixed(2)}"/>`
        : '';
    const bloom = facet.flowerColor
      ? `<rect x="${(x + 1).toFixed(1)}" y="${(height * 0.22).toFixed(1)}" width="${(cell - 4).toFixed(1)}" ` +
        `height="${(height * 0.4).toFixed(1)}" rx="2" fill="${safeColor(facet.flowerColor, '#c9a227')}" ` +
        `opacity="${Math.min(1, 0.5 + facet.flowerDensity).toFixed(2)}"/>`
      : '';
    return bare + foliage + bloom;
  });
  return (
    `<svg class="lg-flodb__svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" role="img" ` +
    `aria-label="The plant’s year">${parts.join('')}</svg>`
  );
}

/**
 * The fruit markers, as a grid of twelve dots laid over the band rather than drawn inside it.
 *
 * The band stretches to whatever width the row is, which is right for the bars — a month is a
 * proportion of the year, not a fixed number of pixels — and wrong for anything meant to be round: a
 * circle in a `preserveAspectRatio="none"` viewBox comes out as a lozenge three times wider than it
 * is tall. Bars stretch, dots do not, so the dots live in their own layer.
 */
function yearDots(
  facets: readonly { fruitColor: string | null; fruitDensity: number }[],
  month: (index: number) => string,
): HTMLElement {
  const grid = el('div', { class: 'lg-flodb__yeardots', attrs: { 'aria-hidden': 'true' } });
  facets.forEach((facet, index) => {
    const cell = el('span', { class: 'lg-flodb__yeardotcell' });
    if (facet.fruitColor) {
      cell.appendChild(
        el('span', {
          class: 'lg-flodb__yeardot',
          title: `Fruit in ${month(index)}`,
          style: { background: safeColor(facet.fruitColor, '#c0433f') },
        }),
      );
    }
    grid.appendChild(cell);
  });
  return grid;
}

/** The selection's colour river — twelve stacked months of colour families. */
function riverSvg(river: readonly { family: string; label: string; share: number }[][]): string {
  const width = 360;
  const height = 76;
  const cell = width / 12;
  const swatches: Record<string, string> = COLOR_FAMILY_SWATCHES;

  const columns = river.map((bands, index) => {
    const x = index * cell;
    let offset = 0;
    return bands
      .map((band) => {
        const bandHeight = band.share * height;
        const y = offset;
        offset += bandHeight;
        // A 2px surface gap between segments rather than a stroke — the stack stays readable without
        // a border drawn around every piece of it.
        return (
          `<rect x="${(x + 1).toFixed(1)}" y="${y.toFixed(1)}" width="${(cell - 2).toFixed(1)}" ` +
          `height="${Math.max(0, bandHeight - 1.5).toFixed(1)}" rx="1.5" ` +
          `fill="${safeColor(swatches[band.family] ?? '#6b7482', '#6b7482')}">` +
          `<title>${escapeText(band.label)} — ${Math.round(band.share * 100)}%</title></rect>`
        );
      })
      .join('');
  });

  return (
    `<svg class="lg-flodb__svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" role="img" ` +
    `aria-label="Colour offered by this selection, month by month">${columns.join('')}</svg>`
  );
}

/** One plant's hardiness span against the zone axis, with the site marked. */
function zoneBarSvg(
  span: { minimum: number; maximum: number } | null,
  siteZone: number | null,
  rating: string,
  localized: boolean,
): string {
  const width = 300;
  const height = 14;
  const at = (zone: number): number =>
    ((Math.max(ZONE_AXIS.minimum, Math.min(ZONE_AXIS.maximum, zone)) - ZONE_AXIS.minimum) /
      (ZONE_AXIS.maximum - ZONE_AXIS.minimum)) *
    width;

  const track = `<rect x="0" y="${(height / 2 - 1).toFixed(1)}" width="${width}" height="2" rx="1" fill="currentColor" opacity="0.1"/>`;
  if (span === null) {
    return (
      `<svg class="lg-flodb__svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" role="img" ` +
      `aria-label="No hardiness range on record">${track}</svg>`
    );
  }

  const left = at(span.minimum);
  const right = at(span.maximum);
  // Status, not category: the bar is tinted only when the site is actually outside what it claims.
  const tone =
    !localized || rating === 'ideal' || rating === 'suitable'
      ? 'var(--lg-accent)'
      : rating === 'marginal'
        ? 'var(--lg-flodb-warn)'
        : 'var(--lg-danger)';
  const bar =
    `<rect x="${left.toFixed(1)}" y="3" width="${Math.max(3, right - left).toFixed(1)}" ` +
    `height="${height - 6}" rx="${(height - 6) / 2}" fill="${tone}" opacity="0.55"/>`;
  const marker =
    siteZone === null
      ? ''
      : `<line x1="${at(siteZone).toFixed(1)}" y1="0" x2="${at(siteZone).toFixed(1)}" y2="${height}" ` +
        `stroke="var(--lg-fg)" stroke-width="1.6" opacity="0.75"/>`;

  return (
    `<svg class="lg-flodb__svg" viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" role="img" ` +
    `aria-label="Hardiness range">${track}${bar}${marker}</svg>`
  );
}

/**
 * The zone axis.
 *
 * Each tick is placed at the fraction {@link zoneBarSvg} would put that zone at, not in an equal
 * column of its own: the axis runs from zone 2 to zone 11, which is nine intervals across ten
 * labels, and a ten-column grid drifts a whole tenth of the width apart from the bars by the far
 * end — enough to read a plant as hardy a zone colder than it is.
 */
function zoneTicks(): HTMLElement[] {
  const span = ZONE_AXIS.maximum - ZONE_AXIS.minimum;
  const axis = el('div', { class: 'lg-flodb__zoneticks' });
  for (let zone = ZONE_AXIS.minimum; zone <= ZONE_AXIS.maximum; zone += 1) {
    axis.appendChild(
      el('span', {
        class: 'lg-flodb__zonetick',
        text: String(zone),
        style: { left: `${(((zone - ZONE_AXIS.minimum) / span) * 100).toFixed(3)}%` },
      }),
    );
  }
  return [el('span', { class: 'lg-flodb__zonespacer' }), axis];
}

/**
 * Height against spread for the whole selection.
 *
 * One hue for every point and shape for the form factor, rather than five colours: at a hundred
 * overlapping marks a five-way categorical palette is unreadable under any kind of colour vision,
 * and shape survives both the overlap and the print-out.
 */
function scatterSvg(
  points: readonly { row: { id: string; name: string; formFactor: FormFactor }; heightMeters: number; spreadMeters: number }[],
  unit: 'meters' | 'feet',
  focusId: string | null,
): string {
  const width = 360;
  const height = 210;
  const padLeft = 34;
  const padBottom = 24;
  const padTop = 10;
  const padRight = 8;

  const maxHeight = Math.max(1, ...points.map((point) => point.heightMeters));
  const maxSpread = Math.max(1, ...points.map((point) => point.spreadMeters));
  const x = (spread: number): number => padLeft + (spread / maxSpread) * (width - padLeft - padRight);
  const y = (tall: number): number => height - padBottom - (tall / maxHeight) * (height - padTop - padBottom);

  const grid = [0.25, 0.5, 0.75, 1]
    .map(
      (fraction) =>
        `<line x1="${padLeft}" y1="${y(maxHeight * fraction).toFixed(1)}" x2="${width - padRight}" ` +
        `y2="${y(maxHeight * fraction).toFixed(1)}" stroke="currentColor" stroke-width="1" opacity="0.07"/>` +
        `<text x="${padLeft - 4}" y="${(y(maxHeight * fraction) + 3).toFixed(1)}" font-size="8" ` +
        `fill="currentColor" opacity="0.5" text-anchor="end">${escapeText(formatLength(maxHeight * fraction, unit))}</text>`,
    )
    .join('');

  const marks = points
    .map((point) => {
      const cx = x(point.spreadMeters);
      const cy = y(point.heightMeters);
      const focused = point.row.id === focusId;
      const fill = focused ? 'var(--lg-accent)' : 'currentColor';
      const opacity = focused ? '1' : '0.42';
      return (
        `<g fill="${fill}" opacity="${opacity}" stroke="var(--lg-panel)" stroke-width="${focused ? 2 : 1.5}">` +
        formGlyphPath(point.row.formFactor, cx, cy, focused ? 6 : 4.4) +
        `<title>${escapeText(point.row.name)} — ${escapeText(formatLength(point.heightMeters, unit))} tall, ` +
        `${escapeText(formatLength(point.spreadMeters, unit))} across</title></g>`
      );
    })
    .join('');

  return (
    `<svg class="lg-flodb__svg lg-flodb__svg--plot" viewBox="0 0 ${width} ${height}" role="img" ` +
    `aria-label="Mature height against spread">` +
    grid +
    `<line x1="${padLeft}" y1="${height - padBottom}" x2="${width - padRight}" y2="${height - padBottom}" ` +
    `stroke="currentColor" opacity="0.22"/>` +
    `<line x1="${padLeft}" y1="${padTop}" x2="${padLeft}" y2="${height - padBottom}" stroke="currentColor" opacity="0.22"/>` +
    marks +
    `<text x="${width - padRight}" y="${height - 6}" font-size="8.5" fill="currentColor" opacity="0.55" ` +
    `text-anchor="end">Spread → ${escapeText(formatLength(maxSpread, unit))}</text>` +
    `<text x="${padLeft - 4}" y="${padTop + 2}" font-size="8.5" fill="currentColor" opacity="0.55" ` +
    `text-anchor="end">Height</text>` +
    `</svg>`
  );
}

/** One glyph per form factor — a triangle for a tree, a circle for a shrub, and so on. */
function formGlyphPath(form: FormFactor, cx: number, cy: number, r: number): string {
  switch (form) {
    case 'tree':
      return `<path d="M${cx} ${cy - r} L${cx + r} ${cy + r * 0.8} L${cx - r} ${cy + r * 0.8} Z"/>`;
    case 'shrub':
      return `<circle cx="${cx}" cy="${cy}" r="${r}"/>`;
    case 'grass':
      return `<rect x="${cx - r * 0.45}" y="${cy - r}" width="${r * 0.9}" height="${r * 2}" rx="${r * 0.45}"/>`;
    case 'perennial':
      return `<path d="M${cx} ${cy - r} L${cx + r} ${cy} L${cx} ${cy + r} L${cx - r} ${cy} Z"/>`;
    case 'groundcover':
      return `<rect x="${cx - r}" y="${cy - r * 0.55}" width="${r * 2}" height="${r * 1.1}" rx="${r * 0.3}"/>`;
  }
}

function formGlyphSvg(form: FormFactor): string {
  return (
    `<svg viewBox="0 0 14 14" width="12" height="12" aria-hidden="true" fill="currentColor">` +
    formGlyphPath(form, 7, 7, 5) +
    `</svg>`
  );
}

function legendItem(label: string, background: string): HTMLElement {
  return el(
    'span',
    { class: 'lg-flodb__legenditem' },
    el('span', { class: 'lg-flodb__legendswatch', style: { background } }),
    el('span', { text: label }),
  );
}

/** Escape the few characters that would end an SVG text node or attribute early. */
function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
